import React, { useState } from 'react';
import { useCharacterStore } from '../../../stores/character';
import advancementsData from '../../../data/advancements.json';
import talentsData from '../../../data/talents.json';
import characteristicCosts from '../../../data/characteristic-costs.json';
import { SkillsService, formatSkillDuration } from '../../../services/SkillsService';

type Career = string;

interface CareerAdvancementTabProps {
  selectedCareer: Career | 'all';
  setSelectedCareer: (career: Career | 'all') => void;
  selectedRulebookIds: string[];
  careerAdvancementData: {
    careers: Career[];
    summary: Record<
      string,
      {
        ranks: Record<
          number,
          { total: number; skills: number; talents: number; characteristics: number }
        >;
      }
    >;
  };
}

export const CareerAdvancementTab: React.FC<CareerAdvancementTabProps> = ({
  selectedCareer,
  setSelectedCareer,
  selectedRulebookIds,
  careerAdvancementData
}) => {
  const { careers } = careerAdvancementData;
  const [expandedCareers, setExpandedCareers] = useState<Record<string, boolean>>({});
  const [expandedRanks, setExpandedRanks] = useState<Record<string, boolean>>({});
  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});
  const character = useCharacterStore((state) => state.character);

  const careerDescriptions: Record<Career, string> = {
    'Arch-Militant': "The Rogue Trader's military commander, master of warfare and tactics",
    'Astropath Transcendent':
      'A sanctioned psyker bound to the Emperor, capable of sending messages across the void',
    Explorator: 'A Tech-Priest of the Adeptus Mechanicus, seeking ancient knowledge and technology',
    Missionary: 'A zealous servant of the Emperor, spreading the Imperial Creed',
    Navigator: 'A mutant with a third eye capable of perceiving the Warp',
    'Rogue Trader': 'A powerful merchant-explorer with a Warrant of Trade',
    Seneschal: "The Rogue Trader's right hand, managing the dynasty's affairs",
    'Void-Master': 'Master of voidships and the treacherous currents of space'
  };

  const displayedCareers = selectedCareer === 'all' ? careers : [selectedCareer];

  const toggleCareer = (career: Career) => {
    setExpandedCareers((prev) => ({ ...prev, [career]: !prev[career] }));
  };

  const toggleRank = (key: string) => {
    setExpandedRanks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Get advancements for a specific career and rank
  const getAdvancementsForRank = (career: Career, rank: number) => {
    interface Advancement {
      career: string;
      rank: number;
      name: string;
      baseName: string;
      cost: number;
      category: string;
      prerequisites: string;
      description: string;
      rulebook: string;
      skillStringId?: string;
      talentStringId?: string;
    }

    return (advancementsData.advancements as Advancement[])
      .filter(
        (a) =>
          a.career === career &&
          a.rank === rank &&
          selectedRulebookIds.includes(a.rulebook || 'rogue-trader-core')
      )
      .sort((a, b) => {
        // Sort by category first (skills, then talents), then by name
        if (a.category !== b.category) {
          return a.category === 'skill' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
  };

  // Check if character meets prerequisites
  const checkPrerequisites = (prereqString: string): { met: boolean; details: string[] } => {
    if (!prereqString || !character) {
      return { met: true, details: [] };
    }

    const details: string[] = [];
    let allMet = true;

    // Parse characteristic requirements (e.g., "Fel 30", "WS 40, BS 35")
    const charPattern = /(WS|BS|S|T|Ag|Int|Per|WP|Fel)\s+(\d+)/gi;
    const matches = prereqString.matchAll(charPattern);

    for (const match of matches) {
      const charName = match[1].toUpperCase();
      const requiredValue = parseInt(match[2]);

      // Map characteristic names to character properties
      const charMap: Record<string, keyof typeof character.characteristics> = {
        WS: 'weaponSkill',
        BS: 'ballisticSkill',
        S: 'strength',
        T: 'toughness',
        AG: 'agility',
        INT: 'intelligence',
        PER: 'perception',
        WP: 'willpower',
        FEL: 'fellowship'
      };

      const charKey = charMap[charName];
      if (charKey) {
        const currentValue = character.characteristics[charKey];
        const met = currentValue >= requiredValue;
        allMet = allMet && met;
        details.push(`${charName} ${requiredValue} (${met ? '✓' : '✗'} Current: ${currentValue})`);
      }
    }

    return { met: allMet, details };
  };

  // Get talent description
  const getTalentDescription = (talentName: string): string => {
    interface TalentDef {
      name: string;
      shortDescription: string;
      fullDescription: string;
    }
    const talent = (talentsData.talents as TalentDef[]).find(
      (t) => t.name.toLowerCase() === talentName.toLowerCase()
    );
    return talent?.fullDescription || talent?.shortDescription || '';
  };

  // Get skill description and duration from SkillsService
  const getSkillInfo = (
    skillStringId: string | undefined
  ): {
    fullDescription: string;
    skillUseDuration: string | undefined;
    specialUses?: Array<{ name: string; description: string }>;
  } | null => {
    if (!skillStringId) return null;

    const skillDef = SkillsService.getSkillById(skillStringId);
    if (!skillDef) return null;

    return {
      fullDescription: skillDef.fullDescription,
      skillUseDuration: skillDef.skillUseDuration,
      specialUses: skillDef.specialUses
    };
  };

  // Toggle expanded description for a specific advancement
  const toggleDescription = (advKey: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click from triggering
    setExpandedDescriptions((prev) => ({ ...prev, [advKey]: !prev[advKey] }));
  };

  const expandAll = () => {
    const newExpandedCareers: Record<string, boolean> = {};
    const newExpanded: Record<string, boolean> = {};
    displayedCareers.forEach((career) => {
      newExpandedCareers[career] = true;
      for (let rank = 1; rank <= 8; rank++) {
        newExpanded[`${career}-rank-${rank}`] = true;
      }
    });
    setExpandedCareers(newExpandedCareers);
    setExpandedRanks(newExpanded);
  };

  const collapseAll = () => {
    setExpandedCareers({});
    setExpandedRanks({});
  };

  return (
    <div>
      {/* Character Info Banner */}
      {character && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                Viewing as: {character.name}
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Career: {character.career.name} | Available XP:{' '}
                {character.experience?.available || 0} | Spent XP:{' '}
                {character.experience?.spent || 0}
              </p>
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400">
              Prerequisites are checked against this character
            </div>
          </div>
        </div>
      )}

      {/* Career Filter */}
      <div className="bg-surface-alt rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-ink mb-2">Select Career</label>
            <select
              value={selectedCareer}
              onChange={(e) => setSelectedCareer(e.target.value as Career | 'all')}
              className="w-full md:w-64 px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Careers</option>
              {careers.map((career) => (
                <option key={career} value={career}>
                  {career}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-2 text-sm bg-surface-active hover:bg-surface-active text-white rounded-md transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Career Tables */}
      <div className="space-y-6">
        {displayedCareers.map((career) => {
          // Get characteristic costs for this career
          const careerId = career.toLowerCase().replace(/\s+/g, '-');
          const careerCharCosts = (characteristicCosts.careers as any)[careerId];
          const isCareerExpanded = expandedCareers[career] ?? displayedCareers.length === 1;

          return (
            <div key={career} className="bg-surface border border-line rounded-lg overflow-hidden">
              {/* Career Header */}
              <button
                type="button"
                onClick={() => toggleCareer(career)}
                className="w-full bg-cobalt p-4 text-left"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-1">{career}</h2>
                    <p className="text-blue-100 text-sm">{careerDescriptions[career as Career]}</p>
                  </div>
                  <svg
                    className={`mt-1 h-5 w-5 flex-shrink-0 text-white transition-transform ${isCareerExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {isCareerExpanded && (
                <>
                  {/* Characteristic Advancement Table */}
                  {careerCharCosts && (
                    <div className="p-4 bg-page/50 border-b border-line">
                      <h3 className="text-lg font-semibold text-ink mb-3">
                        Characteristic Advancement Costs
                      </h3>
                      {careerCharCosts.note && (
                        <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm text-yellow-800 dark:text-yellow-200">
                          ⚠️ {careerCharCosts.note}
                        </div>
                      )}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-surface-alt">
                              <th className="px-3 py-2 text-left font-semibold text-ink border border-line">
                                Characteristic
                              </th>
                              <th className="px-3 py-2 text-center font-semibold text-ink border border-line">
                                Simple
                              </th>
                              <th className="px-3 py-2 text-center font-semibold text-ink border border-line">
                                Intermediate
                              </th>
                              <th className="px-3 py-2 text-center font-semibold text-ink border border-line">
                                Trained
                              </th>
                              <th className="px-3 py-2 text-center font-semibold text-ink border border-line">
                                Expert
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(careerCharCosts.costs).map(
                              ([charKey, costs]: [string, any]) => {
                                const charNames: Record<string, string> = {
                                  weaponSkill: 'Weapon Skill',
                                  ballisticSkill: 'Ballistic Skill',
                                  strength: 'Strength',
                                  toughness: 'Toughness',
                                  agility: 'Agility',
                                  intelligence: 'Intelligence',
                                  perception: 'Perception',
                                  willpower: 'Willpower',
                                  fellowship: 'Fellowship'
                                };
                                return (
                                  <tr key={charKey} className="hover:bg-surface-hover/50">
                                    <td className="px-3 py-2 font-medium text-ink border border-line">
                                      {charNames[charKey] || charKey}
                                    </td>
                                    <td className="px-3 py-2 text-center text-ink border border-line">
                                      {costs.simple} XP
                                    </td>
                                    <td className="px-3 py-2 text-center text-ink border border-line">
                                      {costs.intermediate} XP
                                    </td>
                                    <td className="px-3 py-2 text-center text-ink border border-line">
                                      {costs.trained} XP
                                    </td>
                                    <td className="px-3 py-2 text-center text-ink border border-line">
                                      {costs.expert} XP
                                    </td>
                                  </tr>
                                );
                              }
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-2 text-xs text-ink-dim">
                        <strong>Note:</strong> Advances must be purchased in order (Simple →
                        Intermediate → Trained → Expert). Each advance increases the characteristic
                        by +5.
                      </div>
                    </div>
                  )}

                  {/* Rank Tables */}
                  <div className="divide-y divide-line">
                    {Array.from({ length: 8 }, (_, i) => i + 1).map((rank) => {
                      const rankKey = `${career}-rank-${rank}`;
                      const isExpanded = expandedRanks[rankKey];
                      const advancements = getAdvancementsForRank(career, rank);
                      const skills = advancements.filter((a) => a.category === 'skill');
                      const talents = advancements.filter((a) => a.category === 'talent');

                      return (
                        <div key={rankKey}>
                          {/* Rank Header - Clickable */}
                          <button
                            onClick={() => toggleRank(rankKey)}
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-hover transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-semibold text-ink">Rank {rank}</span>
                              <span className="text-sm text-ink-dim">
                                {skills.length} Skills, {talents.length} Talents
                              </span>
                            </div>
                            <svg
                              className={`w-5 h-5 text-ink-faint transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>

                          {/* Rank Content - Expandable */}
                          {isExpanded && advancements.length > 0 && (
                            <div className="px-4 pb-4">
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-surface-alt">
                                    <tr>
                                      <th className="px-3 py-2 text-left font-medium text-ink">
                                        Type
                                      </th>
                                      <th className="px-3 py-2 text-left font-medium text-ink">
                                        Name
                                      </th>
                                      <th className="px-3 py-2 text-left font-medium text-ink">
                                        Prerequisites
                                      </th>
                                      <th className="px-3 py-2 text-left font-medium text-ink">
                                        Description
                                      </th>
                                      <th className="px-3 py-2 text-right font-medium text-ink">
                                        Cost
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                    {advancements.map((adv, idx) => {
                                      const prereqCheck = checkPrerequisites(adv.prerequisites);
                                      const enhancedDescription =
                                        adv.category === 'talent'
                                          ? getTalentDescription(adv.baseName) || adv.description
                                          : adv.description;

                                      const rowClass =
                                        character && prereqCheck.met && adv.prerequisites
                                          ? 'bg-green-50 dark:bg-green-900/10 hover:bg-green-100 dark:hover:bg-green-900/20'
                                          : 'hover:bg-surface-hover';

                                      return (
                                        <tr key={idx} className={rowClass}>
                                          <td className="px-3 py-2">
                                            <span
                                              className={`inline-block px-2 py-1 text-xs rounded ${
                                                adv.category === 'skill'
                                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                                                  : 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200'
                                              }`}
                                            >
                                              {adv.category}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 font-medium text-ink">
                                            {adv.name}
                                          </td>
                                          <td className="px-3 py-2">
                                            {adv.prerequisites ? (
                                              <div className="space-y-1">
                                                <div className="text-ink-dim">
                                                  {adv.prerequisites}
                                                </div>
                                                {character && prereqCheck.details.length > 0 && (
                                                  <div className="text-xs space-y-0.5">
                                                    {prereqCheck.details.map((detail, i) => (
                                                      <div
                                                        key={i}
                                                        className={
                                                          detail.includes('✓')
                                                            ? 'text-green-600 dark:text-green-400'
                                                            : 'text-red-600 dark:text-red-400'
                                                        }
                                                      >
                                                        {detail}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              <span className="text-ink-faint">—</span>
                                            )}
                                          </td>
                                          <td className="px-3 py-2 text-ink">
                                            {adv.category === 'skill' && adv.skillStringId
                                              ? (() => {
                                                  const skillInfo = getSkillInfo(adv.skillStringId);
                                                  const advKey = `${career}-${rank}-${idx}`;
                                                  const isDescExpanded =
                                                    expandedDescriptions[advKey];

                                                  if (!skillInfo) {
                                                    return enhancedDescription;
                                                  }

                                                  return (
                                                    <div className="space-y-1">
                                                      {skillInfo.skillUseDuration && (
                                                        <span className="inline-block px-2 py-0.5 text-xs rounded bg-surface-active text-ink-dim">
                                                          ⏱️{' '}
                                                          {formatSkillDuration(
                                                            skillInfo.skillUseDuration
                                                          )}
                                                        </span>
                                                      )}

                                                      {!isDescExpanded && (
                                                        <div className="text-sm">
                                                          {skillInfo.fullDescription.length > 150
                                                            ? `${skillInfo.fullDescription.substring(0, 150)}...`
                                                            : skillInfo.fullDescription}
                                                        </div>
                                                      )}

                                                      {isDescExpanded && (
                                                        <div className="space-y-2">
                                                          <div className="text-sm">
                                                            {skillInfo.fullDescription}
                                                          </div>

                                                          {skillInfo.specialUses &&
                                                            skillInfo.specialUses.length > 0 && (
                                                              <div className="mt-2 pt-2 border-t border-line">
                                                                <div className="text-xs font-medium text-ink-dim mb-1">
                                                                  Special Uses:
                                                                </div>
                                                                <ul className="space-y-1">
                                                                  {skillInfo.specialUses.map(
                                                                    (use, useIdx) => (
                                                                      <li
                                                                        key={useIdx}
                                                                        className="text-xs"
                                                                      >
                                                                        <span className="font-medium text-blue-600 dark:text-blue-400">
                                                                          {use.name}:
                                                                        </span>{' '}
                                                                        <span className="text-ink-dim">
                                                                          {use.description}
                                                                        </span>
                                                                      </li>
                                                                    )
                                                                  )}
                                                                </ul>
                                                              </div>
                                                            )}
                                                        </div>
                                                      )}

                                                      {skillInfo.fullDescription.length > 150 ||
                                                      (skillInfo.specialUses &&
                                                        skillInfo.specialUses.length > 0) ? (
                                                        <button
                                                          onClick={(e) =>
                                                            toggleDescription(advKey, e)
                                                          }
                                                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
                                                        >
                                                          {isDescExpanded
                                                            ? '▲ Show less'
                                                            : '▼ Show more'}
                                                        </button>
                                                      ) : null}
                                                    </div>
                                                  );
                                                })()
                                              : enhancedDescription}
                                          </td>
                                          <td className="px-3 py-2 text-right font-medium text-ink">
                                            {adv.cost} XP
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              {character && (
                                <div className="mt-3 p-2 bg-surface-alt rounded text-xs text-ink-dim">
                                  <span className="font-medium">Legend:</span>
                                  <span className="ml-2">
                                    Green highlight = Prerequisites met for current character
                                  </span>
                                  <span className="ml-2">✓ = Requirement met</span>
                                  <span className="ml-2">✗ = Requirement not met</span>
                                </div>
                              )}
                            </div>
                          )}

                          {isExpanded && advancements.length === 0 && (
                            <div className="px-4 pb-4">
                              <p className="text-sm text-ink-faint italic">
                                No advancement data available for this rank
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
